/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGraduationCap, faMapMarkerAlt, faUser, faPhone,
  faEnvelope, faLink, faUpload, faCheckCircle, 
  faInfoCircle, faCheck, faShieldAlt, faBuilding,
  faUsers, faBriefcase, faGlobe, faFileAlt,
  faChartLine, faCertificate, faCalendar, faTimes, faImage, faRocket,
  faIdCard, faHome, faSchool, faChevronDown, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faFacebook, faTwitter, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { buildApiUrl } from '../config/api';
import { useAutoSave } from '../hooks/useAutoSave';
import axios from 'axios';
import PhoneInput from '../components/PhoneInput';
import { useToast } from '../components/Toast';
import { FormFieldValidation, useFormValidation, validateEmail, validateURL, validateYear, validateNumber } from '../components/FormFieldValidation';
import '../styles/RecruiterRegistrationForm.css';
import logoImage from '../assets/FINAL LOGO AK.png';

const ASSOCIATION_TYPES = [
  "Professional Association",
  "Industry Association",
  "Trade Association",
  "Business Association",
  "Chamber of Commerce",
  "Employer Association",
  "Regional Association",
  "National Association",
  "International Association",
  "Other"
];

const INDUSTRIES_SERVED = [
  "Engineering & Technology",
  "Medicine & Healthcare",
  "Business & Management",
  "Finance & Accounting",
  "IT & Computer Science",
  "Law & Legal Services",
  "Hospitality & Tourism",
  "Education & Training",
  "Agriculture & Agribusiness",
  "Manufacturing",
  "Construction & Real Estate",
  "Media & Communications",
  "Retail & E-commerce",
  "Transportation & Logistics",
  "Energy & Utilities",
  "Consulting & Professional Services",
  "Non-profit & Social Services",
  "Other"
];

const AssociationRegistrationForm = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  // Form validation
  const validationRules = {
    associationName: { required: true, minLength: 2 },
    registrationNumber: { required: true },
    yearEstablished: { required: true, year: true },
    associationType: { required: true },
    email: { required: true, email: true },
    phone: { required: true, phone: true },
    website: { required: true, url: true },
    address: { required: true, minLength: 5 },
    city: { required: true },
    state: { required: true },
    country: { required: true },
    contactName: { required: true, minLength: 2 },
    designation: { required: true },
    contactEmail: { required: true, email: true },
    contactPhone: { required: true, phone: true },
    totalMembers: { required: true, number: true, min: 1 },
    totalStaff: { required: true, number: true, min: 1 },
    accreditation: { required: true },
    accreditationNumber: { required: true },
    memberBenefits: { required: true, minLength: 10 },
    about: { required: true, minLength: 20 }
  };
  
  const { errors, touched, handleBlur, handleChange, validateForm, setTouched } = useFormValidation();
  const [submitError, setSubmitError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isIndustriesDropdownOpen, setIsIndustriesDropdownOpen] = useState(false);
  const [industrySearchQuery, setIndustrySearchQuery] = useState('');
  const industriesDropdownRef = useRef(null);

  // Auto-save configuration
  const AUTOSAVE_KEY = `association_registration_${user?.userId || 'temp'}`;

  // Use auto-save hook
  const {
    formData,
    setFormData,
    isSaving,
    saveStatus,
    lastSaveTime,
    lastBackendSaveTime,
    forceSave,
    hasRecentSave,
    clearSavedData
  } = useAutoSave({
    // Basic Information
    associationName: '',
    registrationNumber: '',
    yearEstablished: '',
    associationType: '',
    associationLogo: '',
    
    // Contact Information
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    phone: '',
    email: '',
    website: '',
    linkedin: '',
    facebook: '',
    twitter: '',
    instagram: '',
    
    // Primary Contact Person
    contactName: '',
    designation: '',
    contactEmail: '',
    contactPhone: '',
    
    // Association Details
    totalMembers: '',
    totalStaff: '',
    accreditation: '',
    accreditationNumber: '',
    industriesServed: [],
    memberBenefits: '',
    about: '',
    
    // Agreements
    agreeTerms: false
  },
  AUTOSAVE_KEY,
  1000,
  null,
  true
  );

  const [industries, setIndustries] = useState([]);

  // Persist array-based fields across refreshes
  const ARRAYS_AUTOSAVE_KEY = useMemo(() => `${AUTOSAVE_KEY}_arrays`, [AUTOSAVE_KEY]);

  // Hydrate arrays on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(ARRAYS_AUTOSAVE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed?.industries)) setIndustries(parsed.industries);
      }
    } catch (_) { /* ignore */ }
  }, [ARRAYS_AUTOSAVE_KEY]);

  // Save arrays whenever they change
  useEffect(() => {
    const payload = { industries };
    try {
      localStorage.setItem(ARRAYS_AUTOSAVE_KEY, JSON.stringify(payload));
    } catch (_) { /* ignore quota errors */ }
  }, [industries, ARRAYS_AUTOSAVE_KEY]);

  // Update logo preview when formData.associationLogo changes
  useEffect(() => {
    if (formData.associationLogo && formData.associationLogo !== logoPreview) {
      setLogoPreview(formData.associationLogo);
    }
  }, [formData.associationLogo]);

  // Close industries dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (industriesDropdownRef.current && !industriesDropdownRef.current.contains(event.target)) {
        setIsIndustriesDropdownOpen(false);
        setIndustrySearchQuery('');
      }
    };

    if (isIndustriesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isIndustriesDropdownOpen]);

  // Filter industries based on search query
  const filteredIndustries = useMemo(() => {
    if (!industrySearchQuery.trim()) {
      return INDUSTRIES_SERVED;
    }
    return INDUSTRIES_SERVED.filter(industry =>
      industry.toLowerCase().includes(industrySearchQuery.toLowerCase())
    );
  }, [industrySearchQuery]);

  const handleIndustryToggle = (industry) => {
    setIndustries(prev => {
      const updated = prev.includes(industry)
        ? prev.filter(i => i !== industry)
        : [...prev, industry];
      updateProgress();
      return updated;
    });
  };

  const updateProgress = useCallback(() => {
    const requiredFields = [
      formData.associationName, formData.registrationNumber, formData.yearEstablished,
      formData.associationType, formData.address, formData.city, formData.state,
      formData.country, formData.phone, formData.email, formData.website,
      formData.contactName, formData.designation, formData.contactEmail,
      formData.contactPhone, formData.totalMembers, formData.totalStaff,
      formData.accreditation, formData.accreditationNumber,
      formData.memberBenefits, formData.about
    ];

    const filledFields = requiredFields.filter(field => {
      return field && field.toString().trim() !== '';
    }).length;

    // Count array fields
    let arrayFieldsCount = 0;
    if (industries.length > 0) arrayFieldsCount++;

    // Count terms checkboxes
    let termsCount = 0;
    if (formData.agreeTerms) termsCount++;

    // Total: 21 required fields + 1 array + 1 terms = 23 total
    const totalRequired = 23;
    const totalFilled = filledFields + arrayFieldsCount + termsCount;

    const progressPercent = Math.min((totalFilled / totalRequired) * 100, 100);
    setProgress(progressPercent);
  }, [formData, industries]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Real-time validation
      if (touched[name] && validationRules[name]) {
        handleChange(name, value, validationRules[name]);
      }
    }
    
    updateProgress();
  }, [updateProgress, setFormData, touched, validationRules, handleChange]);


  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showWarning('File size must be less than 2MB');
        return;
      }
      if (!file.type.match(/^image\/(png|jpg|jpeg|svg)$/i)) {
        showWarning('Only PNG, JPG, and SVG images are allowed');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const logoData = e.target.result;
        setLogoPreview(logoData);
        setFormData(prev => ({
          ...prev,
          associationLogo: logoData
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCompleteProfile = async () => {
    if (isLoading || isSaving) return;
    try {
      await forceSave();
    } catch (e) {
      console.error('Flush before complete profile failed:', e);
    }
    const formEl = document.getElementById('associationForm');
    if (formEl) {
      if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit();
      } else {
        formEl.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  };

  const handleGoToDashboard = async () => {
    try {
      await forceSave();
    } catch (e) {
      console.error('Failed to flush form before dashboard:', e);
    }
    navigate('/association-dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError('');

    // Mark all fields as touched for validation
    setTouched(Object.keys(validationRules).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    // Validate form
    const isValid = validateForm(formData, validationRules);
    
    if (!isValid) {
      showError('Please fix the errors in the form before submitting');
      setIsLoading(false);
      return;
    }

    // Additional validations
    if (!formData.agreeTerms) {
      setSubmitError('Please accept the terms and conditions');
      showWarning('Please accept the terms and conditions');
      setIsLoading(false);
      return;
    }

    if (industries.length === 0) {
      setSubmitError('Please select at least one industry/sector served');
      showWarning('Please select at least one industry/sector served');
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (typeof formData[key] === 'boolean') {
          submitData.append(key, formData[key].toString());
        } else if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      // Add industries
      submitData.append('industriesServed', JSON.stringify(industries));

      // Add logo file if uploaded
      if (logoFile) {
        submitData.append('associationLogo', logoFile);
      }

      const response = await axios.post(
        buildApiUrl('/api/association/complete-registration'),
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Clear saved form data on successful submission
        clearSavedData();
        
        // Update localStorage with profile completion status
        localStorage.setItem('profileCompleted', 'true');
        localStorage.setItem('hasCompletedProfile', 'true');
        
        // Update user context to mark profile as completed
        if (typeof updateUser === 'function') {
          updateUser({ 
            profileCompleted: true,
            hasCompletedProfile: true
          });
        }
        
        // Show success message
        showSuccess('Association registration completed successfully! Redirecting to your dashboard...');
        
        // Force refresh of user data to ensure profile completion is detected
        setTimeout(() => {
          sessionStorage.clear();
          localStorage.setItem('profileCompleted', 'true');
          localStorage.setItem('hasCompletedProfile', 'true');
          window.location.href = '/association-dashboard';
        }, 1000);
      } else {
        setSubmitError(response.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(error.response?.data?.message || 'Failed to complete registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="recruiter-details-comprehensive">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .recruiter-details-comprehensive {
          max-width: 100% !important;
        }
        .recruiter-header {
          width: 100% !important;
          max-width: 100% !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        .recruiter-header .header-container {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          padding: 12px 20px 12px 10px !important;
          max-width: 100% !important;
          margin: 0 !important;
          width: 100% !important;
        }
        .recruiter-header .header-left {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 12px !important;
          margin-right: auto !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
          flex: 0 0 auto !important;
          order: 1 !important;
        }
        .recruiter-header .header-left .logo-icon {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-left .logo-text {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-right {
          margin-left: auto !important;
          flex: 0 0 auto !important;
          order: 2 !important;
        }
      `}</style>
      
      {/* Header */}
      <header className="recruiter-header">
        <div className="header-container">
          <div className="header-left" onClick={() => navigate('/association-dashboard')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="logo-icon">
              <img src={logoImage} alt="AksharJobs Logo" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            </div>
            <div className="logo-text">
              <h3 className="logo-title" style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>AksharJobs</h3>
              <p className="logo-subtitle" style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>Association Registration</p>
            </div>
          </div>
          <div className="header-right">
            <div className="header-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div className="auto-save-status" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                {isSaving && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#f39c12' }}>
                    <div className="spinner" style={{ width: '12px', height: '12px', border: '2px solid #f39c12', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <span>Auto-saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#27ae60' }}>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Auto-saved {lastSaveTime && `at ${lastSaveTime.toLocaleTimeString()}`}</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#e74c3c' }}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Save failed - data preserved locally</span>
                  </div>
                )}
                {saveStatus === 'loaded' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#3498db' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Previous data restored</span>
                  </div>
                )}
                {!isSaving && !saveStatus && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6c757d' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Auto-save enabled</span>
                  </div>
                )}
              </div>
              <button
                className="complete-profile-btn"
                type="button"
                onClick={handleCompleteProfile}
                disabled={isSaving || isLoading}
                style={{
                  background: (isSaving || isLoading) ? '#bdc3c7' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: (isSaving || isLoading) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#2980b9';
                }}
                onMouseLeave={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#3498db';
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} /> {(isSaving || isLoading) ? 'Submitting...' : 'Complete profile'}
              </button>
              <button
                className="go-to-dashboard-btn"
                type="button"
                onClick={handleGoToDashboard}
                disabled={isSaving || isLoading}
                style={{
                  background: (isSaving || isLoading) ? '#bdc3c7' : '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: (isSaving || isLoading) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#4b5563';
                }}
                onMouseLeave={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#6b7280';
                }}
              >
                <FontAwesomeIcon icon={faRocket} /> {isSaving ? 'Saving...' : 'Go to dashboard'}
              </button>
              <button
                className="clear-form-btn"
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all saved form data? This cannot be undone.')) {
                    clearSavedData();
                    window.location.reload();
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #e74c3c',
                  color: '#e74c3c',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease'
                }}
              >
                Clear Form Data
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="recruiter-main">
        <div className="recruiter-container">
          <div className="recruiter-form-card">
            {/* Main Form */}
            <div className="recruiter-form-container">
              <form id="associationForm" onSubmit={handleSubmit}>
                {/* Enhanced Progress Section */}
                <div className="progress-section-comprehensive" style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '30px',
                  color: '#000000',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '2px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="progress-header-comprehensive" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#000000' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '10px', color: '#0d9488' }} />
                      Registration Progress
                    </h2>
                    <div style={{
                      background: '#f5f5f5',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#000000'
                    }}>
                      {Math.round(progress)}%
                    </div>
                  </div>
                  
                  <div className="progress-bar-comprehensive" style={{
                    background: '#f5f5f5',
                    height: '12px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    marginBottom: '15px',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}>
                    <div 
                      className="progress-fill-comprehensive" 
                      style={{ 
                        width: `${progress}%`,
                        height: '100%',
                        background: '#0d9488',
                        borderRadius: '6px',
                        transition: 'width 0.5s ease-in-out',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        height: '100%',
                        width: '20px',
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 100%)',
                        borderRadius: '0 6px 6px 0'
                      }}></div>
                    </div>
                  </div>

                  {/* Progress Status Message */}
                  <div style={{
                    fontSize: '14px',
                    opacity: 0.9,
                    textAlign: 'center',
                    marginBottom: '10px',
                    color: '#000000'
                  }}>
                    {Math.round(progress) === 100 ? (
                      <span style={{ color: '#0d9488', fontWeight: '600' }}>
                        🎉 Profile Complete! Ready to submit.
                      </span>
                    ) : Math.round(progress) >= 80 ? (
                      <span style={{ color: '#000000' }}>
                        Almost there! Just a few more fields to complete.
                      </span>
                    ) : Math.round(progress) >= 50 ? (
                      <span style={{ color: '#000000' }}>
                        Good progress! Keep filling out the form.
                      </span>
                    ) : (
                      <span style={{ color: '#000000' }}>
                        Let's get started! Fill out your association information.
                      </span>
                    )}
                  </div>

                  {/* Quick Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '10px',
                    fontSize: '12px',
                    marginBottom: '15px',
                    color: '#000000'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>6</div>
                      <div style={{ color: '#000000' }}>Sections</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>25+</div>
                      <div style={{ color: '#000000' }}>Fields</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>Auto-save</div>
                      <div style={{ color: '#000000' }}>Enabled</div>
                    </div>
                  </div>

                  {/* Section Completion Checklist */}
                  {Math.round(progress) < 100 && (
                    <div style={{
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#000000' }}>
                        Complete these sections:
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: '6px'
                      }}>
                        {[
                          { key: 'basic', name: 'Basic Info', icon: '🏛️', completed: formData.associationName && formData.associationType && formData.registrationNumber },
                          { key: 'contact', name: 'Contact Info', icon: '📧', completed: formData.email && formData.phone && formData.address },
                          { key: 'person', name: 'Contact Person', icon: '👤', completed: formData.contactName && formData.designation && formData.contactEmail },
                          { key: 'details', name: 'Association Details', icon: '📄', completed: formData.totalMembers && formData.totalStaff && formData.accreditation },
                          { key: 'industries', name: 'Industries', icon: '🏢', completed: industries.length > 0 },
                          { key: 'terms', name: 'Terms', icon: '✅', completed: formData.agreeTerms }
                        ].map(section => (
                          <div key={section.key} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: section.completed ? 0.6 : 1
                          }}>
                            <span style={{
                              color: section.completed ? '#0d9488' : '#000000',
                              fontSize: '14px'
                            }}>
                              {section.completed ? '✅' : section.icon}
                            </span>
                            <span style={{
                              textDecoration: section.completed ? 'line-through' : 'none',
                              opacity: section.completed ? 0.7 : 1,
                              color: '#000000'
                            }}>
                              {section.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {submitError && (
                  <div className="error-message" style={{ marginBottom: '20px', padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '6px', color: '#c33' }}>
                    <FontAwesomeIcon icon={faInfoCircle} /> {submitError}
                  </div>
                )}

                {/* Association Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faUsers} />
                    Association Information
                  </h2>

                  {logoPreview && (
                    <div className="profile-photo-preview-comprehensive" style={{ display: 'block' }}>
                      <img src={logoPreview} alt="Association Logo" />
                    </div>
                  )}

                  <div className="form-group-comprehensive">
                    <label className="file-upload-comprehensive">
                      <FontAwesomeIcon icon={faImage} />
                      <div className="file-upload-text-comprehensive">
                        <strong>Upload Association Logo</strong>
                        <p>Click to browse or drag and drop (PNG, JPG, SVG, max 2MB)</p>
                      </div>
                      <input type="file" accept="image/*" onChange={handleLogoChange} />
                    </label>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Association Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="associationName"
                      value={formData.associationName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('associationName')}
                      placeholder="Enter association name"
                      required
                      style={{
                        borderColor: touched.associationName && errors.associationName ? '#ef4444' : 
                                   touched.associationName && !errors.associationName ? '#10b981' : undefined
                      }}
                    />
                    <FormFieldValidation
                      fieldName="associationName"
                      value={formData.associationName}
                      touched={touched}
                      errors={errors}
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Association Type <span className="required">*</span></label>
                      <select
                        name="associationType"
                        value={formData.associationType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Type</option>
                        {ASSOCIATION_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Year Established <span className="required">*</span></label>
                      <input
                        type="number"
                        name="yearEstablished"
                        value={formData.yearEstablished}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('yearEstablished')}
                        placeholder="YYYY"
                        required
                        min="1900"
                        max={new Date().getFullYear()}
                        style={{
                          borderColor: touched.yearEstablished && errors.yearEstablished ? '#ef4444' : 
                                     touched.yearEstablished && !errors.yearEstablished ? '#10b981' : undefined
                        }}
                      />
                      <FormFieldValidation
                        fieldName="yearEstablished"
                        value={formData.yearEstablished}
                        touched={touched}
                        errors={errors}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Registration Number <span className="required">*</span></label>
                    <input
                      type="text"
                      name="registrationNumber"
                      value={formData.registrationNumber}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('registrationNumber')}
                      placeholder="e.g., INST/2024/001"
                      required
                      style={{
                        borderColor: touched.registrationNumber && errors.registrationNumber ? '#ef4444' : 
                                   touched.registrationNumber && !errors.registrationNumber ? '#10b981' : undefined
                      }}
                    />
                    <FormFieldValidation
                      fieldName="registrationNumber"
                      value={formData.registrationNumber}
                      touched={touched}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faEnvelope} />
                    Contact Information
                  </h2>

                  <div className="form-group-comprehensive">
                    <label>Address <span className="required">*</span></label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      placeholder="Complete address with street, area"
                      rows="3"
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>City <span className="required">*</span></label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        placeholder="City name"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>State/County <span className="required">*</span></label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        placeholder="State/County"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="Postal/ZIP code"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Country <span className="required">*</span></label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      placeholder="Country"
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Phone Number <span className="required">*</span></label>
                      <PhoneInput
                        value={formData.phone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, phone: value }));
                          updateProgress();
                          if (touched.phone && validationRules.phone) {
                            handleChange('phone', value, validationRules.phone);
                          }
                        }}
                        onBlur={() => handleBlur('phone')}
                        required
                        placeholder="+254 XXX XXX XXX"
                        style={{
                          borderColor: touched.phone && errors.phone ? '#ef4444' : 
                                     touched.phone && !errors.phone ? '#10b981' : undefined
                        }}
                      />
                      <FormFieldValidation
                        fieldName="phone"
                        value={formData.phone}
                        touched={touched}
                        errors={errors}
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Email Address <span className="required">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('email')}
                        required
                        placeholder="contact@institute.com"
                        style={{
                          borderColor: touched.email && errors.email ? '#ef4444' : 
                                     touched.email && !errors.email ? '#10b981' : undefined
                        }}
                      />
                      <FormFieldValidation
                        fieldName="email"
                        value={formData.email}
                        touched={touched}
                        errors={errors}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Website <span className="required">*</span></label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('website')}
                      required
                      placeholder="https://www.yourwebsite.com"
                      style={{
                        borderColor: touched.website && errors.website ? '#ef4444' : 
                                   touched.website && !errors.website ? '#10b981' : undefined
                      }}
                    />
                    <FormFieldValidation
                      fieldName="website"
                      value={formData.website}
                      touched={touched}
                      errors={errors}
                    />
                    />
                  </div>
                </div>

                {/* Primary Contact Person Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faUser} />
                    Primary Contact Person
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Full Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleInputChange}
                        required
                        placeholder="Full name"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Designation <span className="required">*</span></label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Director, Principal"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Contact Email <span className="required">*</span></label>
                      <input
                        type="email"
                        name="contactEmail"
                        value={formData.contactEmail}
                        onChange={handleInputChange}
                        required
                        placeholder="person@institute.com"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Contact Phone <span className="required">*</span></label>
                      <PhoneInput
                        value={formData.contactPhone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, contactPhone: value }));
                          updateProgress();
                        }}
                        required
                        placeholder="+254 XXX XXX XXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Association Details Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faFileAlt} />
                    Association Details
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Total Members <span className="required">*</span></label>
                      <input
                        type="number"
                        name="totalMembers"
                        value={formData.totalMembers}
                        onChange={handleInputChange}
                        required
                        min="1"
                        placeholder="Approximate number of members"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Total Staff <span className="required">*</span></label>
                      <input
                        type="number"
                        name="totalStaff"
                        value={formData.totalStaff}
                        onChange={handleInputChange}
                        required
                        min="1"
                        placeholder="Number of staff members"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Accreditation Body <span className="required">*</span></label>
                      <input
                        type="text"
                        name="accreditation"
                        value={formData.accreditation}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Commission for University Education"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Accreditation Number <span className="required">*</span></label>
                      <input
                        type="text"
                        name="accreditationNumber"
                        value={formData.accreditationNumber}
                        onChange={handleInputChange}
                        required
                        placeholder="Accreditation reference number"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Industries/Sectors Served <span className="required">*</span></label>
                    
                    {/* Compact Dropdown Input */}
                    <div ref={industriesDropdownRef} style={{ position: 'relative' }}>
                      <div
                        onClick={() => setIsIndustriesDropdownOpen(!isIndustriesDropdownOpen)}
                        style={{
                          width: '100%',
                          minHeight: '40px',
                          padding: '8px 12px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          backgroundColor: '#ffffff',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          ...(isIndustriesDropdownOpen && {
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          })
                        }}
                      >
                        <span style={{
                          color: industries.length === 0 ? '#999' : '#333',
                          flex: 1
                        }}>
                          {industries.length === 0 
                            ? 'Select industries/sectors...' 
                            : `${industries.length} industr${industries.length !== 1 ? 'ies' : 'y'} selected`}
                        </span>
                        <FontAwesomeIcon 
                          icon={faChevronDown} 
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            transition: 'transform 0.3s ease',
                            transform: isIndustriesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      </div>

                      {/* Dropdown Menu */}
                      {isIndustriesDropdownOpen && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          zIndex: 1000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          {/* Search Input */}
                          <div style={{
                            padding: '8px',
                            borderBottom: '1px solid #e2e8f0',
                            background: '#f8f9fa'
                          }}>
                            <div style={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <FontAwesomeIcon 
                                icon={faSearch} 
                                style={{
                                  position: 'absolute',
                                  left: '12px',
                                  color: '#999',
                                  fontSize: '14px'
                                }}
                              />
                              <input
                                type="text"
                                placeholder="Search industries..."
                                value={industrySearchQuery}
                                onChange={(e) => setIndustrySearchQuery(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px 8px 36px',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  outline: 'none'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#667eea';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                }}
                              />
                            </div>
                          </div>

                          {/* Options List */}
                          <div style={{
                            maxHeight: '240px',
                            overflowY: 'auto',
                            padding: '4px 0'
                          }}>
                            {filteredIndustries.length === 0 ? (
                              <div style={{
                                padding: '12px',
                                textAlign: 'center',
                                color: '#999',
                                fontSize: '14px'
                              }}>
                                No industries found
                              </div>
                            ) : (
                              filteredIndustries.map(industry => {
                                const isSelected = industries.includes(industry);
                                return (
                                  <div
                                    key={industry}
                                    onClick={() => handleIndustryToggle(industry)}
                                    style={{
                                      padding: '10px 12px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                      transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected) {
                                        e.target.style.backgroundColor = '#f5f5f5';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSelected) {
                                        e.target.style.backgroundColor = 'transparent';
                                      }
                                    }}
                                  >
                                    <div style={{
                                      width: '18px',
                                      height: '18px',
                                      border: `2px solid ${isSelected ? '#1976d2' : '#ccc'}`,
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: isSelected ? '#1976d2' : 'transparent',
                                      flexShrink: 0
                                    }}>
                                      {isSelected && (
                                        <FontAwesomeIcon 
                                          icon={faCheck} 
                                          style={{
                                            color: '#ffffff',
                                            fontSize: '10px'
                                          }}
                                        />
                                      )}
                                    </div>
                                    <span style={{
                                      color: isSelected ? '#1976d2' : '#333',
                                      fontWeight: isSelected ? '500' : '400'
                                    }}>
                                      {industry}
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Selected Industries Chips - Display Below Dropdown */}
                    {industries.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        marginTop: '10px',
                        minHeight: '32px'
                      }}>
                        {industries.map(industry => (
                          <span
                            key={industry}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 10px',
                              background: '#e3f2fd',
                              color: '#1976d2',
                              borderRadius: '16px',
                              fontSize: '12px',
                              fontWeight: '500',
                              border: '1px solid #bbdefb'
                            }}
                          >
                            {industry}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleIndustryToggle(industry);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: '0',
                                marginLeft: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                lineHeight: 1,
                                width: '16px',
                                height: '16px'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.color = '#1565c0';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.color = '#1976d2';
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Member Benefits <span className="required">*</span></label>
                    <textarea
                      name="memberBenefits"
                      value={formData.memberBenefits}
                      onChange={handleInputChange}
                      required
                      placeholder="Describe the benefits and services your association provides to members"
                      rows="3"
                      maxLength="1000"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>{formData.memberBenefits?.length || 0}/1000 characters</small>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>About the Association <span className="required">*</span></label>
                    <textarea
                      name="about"
                      value={formData.about}
                      onChange={handleInputChange}
                      required
                      placeholder="Brief description of your association, mission, achievements, and unique features (max 500 words)"
                      rows="4"
                      maxLength="3000"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>{formData.about.length}/3000 characters</small>
                  </div>
                </div>

                {/* Social Media & Optional Info Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faGlobe} />
                    Social Media & Additional Information
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faLinkedin} style={{ color: '#0077B5', marginRight: '8px' }} />
                        LinkedIn Profile
                      </label>
                      <input
                        type="url"
                        name="linkedin"
                        value={formData.linkedin}
                        onChange={handleInputChange}
                        placeholder="https://linkedin.com/company/your-institute"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faFacebook} style={{ color: '#1877F2', marginRight: '8px' }} />
                        Facebook Page
                      </label>
                      <input
                        type="url"
                        name="facebook"
                        value={formData.facebook}
                        onChange={handleInputChange}
                        placeholder="https://facebook.com/your-institute"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faTwitter} style={{ color: '#1DA1F2', marginRight: '8px' }} />
                        Twitter/X Handle
                      </label>
                      <input
                        type="text"
                        name="twitter"
                        value={formData.twitter}
                        onChange={handleInputChange}
                        placeholder="@institutename"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faInstagram} style={{ color: '#E4405F', marginRight: '8px' }} />
                        Instagram Handle
                      </label>
                      <input
                        type="text"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleInputChange}
                        placeholder="@institutename"
                      />
                    </div>
                  </div>
                </div>

                {/* Terms & Agreements Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Terms & Agreements
                  </h2>

                  <div className="checkbox-group" style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    marginBottom: '15px'
                  }}>
                    <input 
                      type="checkbox" 
                      id="agreeTerms" 
                      name="agreeTerms" 
                      required
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      style={{ 
                        marginTop: '3px',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <label htmlFor="agreeTerms" style={{ 
                      textAlign: 'left',
                      margin: 0,
                      cursor: 'pointer',
                      flex: 1
                    }}>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. I confirm that all information provided is accurate and I have the authority to register this association. <span className="required">*</span></label>
                  </div>

                  {/* Admin Approval Message */}
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      style={{
                        color: '#1976d2',
                        fontSize: '18px',
                        marginTop: '2px',
                        flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1976d2',
                        marginBottom: '6px'
                      }}>
                        Account Approval Process
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#424242',
                        lineHeight: '1.6'
                      }}>
                        Complete your profile <strong>80%</strong>. If it is <strong>80% completed</strong>, it will be reviewed and then approved by our admin team within <strong>24 hours</strong>. You will receive an email notification once your account is approved. Please check your <strong>spam folder</strong> if you don't see the email in your inbox.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Section */}
                <div className="submit-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {submitError && (
                    <div className="error-message" style={{ marginBottom: '12px', color: '#e74c3c', textAlign: 'center' }}>
                      {submitError}
                    </div>
                  )}
                  <button type="submit" className="submit-btn" disabled={isLoading} style={{ margin: '0 auto' }}>
                    <FontAwesomeIcon icon={faCheckCircle} /> 
                    {isLoading ? 'Submitting...' : 'Complete Association Registration'}
                  </button>
                  <p style={{ marginTop: '8px', color: '#666', fontSize: '12px', textAlign: 'center' }}>
                    Your account will be reviewed and activated within 24-48 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AssociationRegistrationForm;

